import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import type { BillingDescriptor } from '@/types'

export interface BillingLabelProps {
  billing: BillingDescriptor
  className?: string
}

function currencySymbol(currency?: string): string {
  switch ((currency || '').toUpperCase()) {
    case 'CNY':
    case 'RMB':
      return '¥'
    case 'USD':
      return '$'
    case 'EUR':
      return '€'
    default:
      return currency ? `${currency} ` : ''
  }
}

function fmt(value: number | null | undefined, sym: string): string {
  if (value === null || value === undefined) return '—'
  // Trim trailing zeros while keeping up to 4 significant decimals.
  return `${sym}${Number(value.toFixed(4))}`
}

/**
 * Renders a model's unit price, shaped per `billing.billing_mode`. Reads the
 * catalog billing descriptor directly (no legacy flattening). Unit labels are
 * i18n-keyed; the currency symbol follows `billing.currency` (the catalog
 * normalizes to CNY at the API boundary, so this is usually ¥).
 */
export function BillingLabel({ billing, className }: BillingLabelProps) {
  const { t } = useI18n()
  const base = cn('inline-flex items-baseline gap-1 text-sm text-muted-foreground', className)

  switch (billing.billing_mode) {
    case 'per_token': {
      const sym = currencySymbol(billing.currency)
      return (
        <span className={base}>
          <span className="text-foreground font-medium">{fmt(billing.input_per_million, sym)}</span>
          <span className="text-xs">{t('modelSelect.billing.in')}</span>
          <span aria-hidden>·</span>
          <span className="text-foreground font-medium">{fmt(billing.output_per_million, sym)}</span>
          <span className="text-xs">{t('modelSelect.billing.outPer1M')}</span>
        </span>
      )
    }
    case 'per_image': {
      const sym = currencySymbol(billing.currency)
      return (
        <span className={base}>
          <span className="text-foreground font-medium">{fmt(billing.cost_per_image, sym)}</span>
          <span className="text-xs">{t('modelSelect.billing.perImage')}</span>
        </span>
      )
    }
    case 'per_character': {
      const sym = currencySymbol(billing.currency)
      return (
        <span className={base}>
          <span className="text-foreground font-medium">{fmt(billing.cost_per_1k_chars, sym)}</span>
          <span className="text-xs">{t('modelSelect.billing.per1kChars')}</span>
        </span>
      )
    }
    case 'per_second': {
      const sym = currencySymbol(billing.currency)
      return (
        <span className={base}>
          <span className="text-foreground font-medium">{fmt(billing.cost_per_minute, sym)}</span>
          <span className="text-xs">{t('modelSelect.billing.perMin')}</span>
        </span>
      )
    }
    case 'free':
      return <span className={base}><span className="text-success font-medium">{t('modelSelect.billing.free')}</span></span>
    default:
      return <span className={base}>—</span>
  }
}

/**
 * Maps a billing descriptor to its i18n key suffix under
 * `modelSelect.billingMode.*`, for the billing-mode chip. Consumers translate
 * via `t('modelSelect.billingMode.' + billingModeKey(billing))`.
 */
export function billingModeKey(billing: BillingDescriptor): string {
  switch (billing.billing_mode) {
    case 'per_token':
      return 'perToken'
    case 'per_image':
      return 'perImage'
    case 'per_character':
      return 'perCharacter'
    case 'per_second':
      return 'perSecond'
    case 'free':
      return 'free'
    default:
      return 'unknown'
  }
}
